import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import useCustomizationSummary from '../../CustomizationSummary/useCustomizationSummary';
import useFetchCurrentVehicleFinancialCalculatorSummary from '../../../core/apiHooks/useFetchSummary';
import { EV_FILTER_NAME, getLuxuryTaxMinimum } from '../../../utils/constants';
import { OptionsGroupItemType, OptionsLabel, optionsGroupItemKeys } from './utils';
import { primaryCalculatorId, selectCheckedModalWarrantyCard, selectPaymentMethod, selectPrimaryCalculatorPaymentOptions, } from '../../../core/reducers/financialCalculators';
import { selectCurrentVehicleFromVehicles } from '../../../core/reducers/vehicles';
import { useAppSelector } from '../../../core/utils';
import { useFetchCurrentVehicleFinancialCalculatorPayment } from '../../../core/apiHooks/useFetchPayment';
import { useLanguage, useUserOrDefaultProvince } from '../../../utils/sitecoreContext';
const usePriceBreakdownGroups = () => {
    var _a, _b, _c;
    const vehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const provinceCode = useUserOrDefaultProvince();
    const isVehicleEV = ((_a = vehicle === null || vehicle === void 0 ? void 0 : vehicle.bodyType) === null || _a === void 0 ? void 0 : _a.includes(EV_FILTER_NAME)) || ((_c = (_b = vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters) === null || _b === void 0 ? void 0 : _b.allFilters) === null || _c === void 0 ? void 0 : _c.includes(EV_FILTER_NAME));
    const LUXURY_TAX_MINIMUM = getLuxuryTaxMinimum(provinceCode, isVehicleEV || false);
    const { t } = useTranslation();
    const language = useLanguage();
    const { summary } = useFetchCurrentVehicleFinancialCalculatorSummary(primaryCalculatorId);
    const { data } = useFetchCurrentVehicleFinancialCalculatorPayment(primaryCalculatorId);
    const { includeFees } = useAppSelector(selectPrimaryCalculatorPaymentOptions);
    const { accessories, wheels, charging, protections, warranty } = useCustomizationSummary();
    function getSummaryGroupItemByKey(key) {
        return summary === null || summary === void 0 ? void 0 : summary.find(summaryItem => summaryItem.key === key);
    }
    const checkedModalWarrantyCard = useAppSelector(state => selectCheckedModalWarrantyCard(state, primaryCalculatorId));
    const paymentMethod = useAppSelector(state => selectPaymentMethod(state, primaryCalculatorId));
    function generateAccessorySummaryContent(accessory) {
        var _a, _b;
        if (accessory && !Array.isArray(accessory)) {
            const selectedWarranty = accessory;
            return [
                {
                    title: `${checkedModalWarrantyCard !== null && checkedModalWarrantyCard !== void 0 ? checkedModalWarrantyCard : selectedWarranty.bapType} - ${(_a = selectedWarranty.name) === null || _a === void 0 ? void 0 : _a.replace('*', '')}`,
                    value: selectedWarranty.priceText,
                    unit: 'text',
                    dataTestId: `cy-breakdown-subItem-child-${selectedWarranty.id}`,
                },
            ];
        }
        return ((_b = accessory === null || accessory === void 0 ? void 0 : accessory.map(({ name, priceText, isIncluded, id }) => ({
            title: name,
            value: isIncluded ? formatPrice(0, language, 2) : priceText,
            unit: 'text',
            dataTestId: `cy-breakdown-subItem-child-${id}`,
        }))) !== null && _b !== void 0 ? _b : []);
    }
    function getSubGroupContent(key) {
        switch (optionsGroupItemKeys[key]) {
            case OptionsGroupItemType.ACCESSORIES:
                return [
                    ...generateAccessorySummaryContent(accessories),
                    ...generateAccessorySummaryContent(wheels),
                    ...generateAccessorySummaryContent(charging),
                ];
            case OptionsGroupItemType.PROTECTION:
                return generateAccessorySummaryContent(protections);
            case OptionsGroupItemType.WARRANTY:
                return generateAccessorySummaryContent(warranty);
            default:
                return [];
        }
    }
    const generateContentForGroup = (group, key) => {
        var _a;
        return ({
            title: group === null || group === void 0 ? void 0 : group.title,
            value: group === null || group === void 0 ? void 0 : group.value,
            unit: group === null || group === void 0 ? void 0 : group.unit,
            dataTestId: `cy-breakdown-header-${key}`,
            tooltipText: group === null || group === void 0 ? void 0 : group.description,
            content: (_a = group === null || group === void 0 ? void 0 : group.summary) === null || _a === void 0 ? void 0 : _a.map(summaryItem => {
                const customTooltip = summaryItem.key === 'LeaseLevy_Dealer_Fees' ? t('Pages.Bap.dealerAdminFeeTooltipText') : '';
                return {
                    title: summaryItem.title,
                    value: summaryItem.value,
                    unit: summaryItem.unit,
                    tooltipText: customTooltip || summaryItem.description,
                    dataTestId: `cy-breakdown-subItem-${key}-${summaryItem.key}`,
                    content: getSubGroupContent(summaryItem.key),
                };
            }),
        });
    };
    const generateSummaryForOptionsGroup = (optionsKeysInRequest) => {
        let optionsTotal = 0;
        const optionsSummary = [];
        optionsKeysInRequest.forEach((key) => {
            const group = getSummaryGroupItemByKey(key);
            if (group) {
                const value = typeof group.value === 'number' ? group.value : 0;
                optionsTotal += value;
                optionsSummary.push({
                    key: `${capitalize(paymentMethod)}${key}`,
                    title: group.title,
                    unit: group.unit,
                    value,
                    description: '',
                    summary: [],
                });
            }
        });
        return { optionsTotal, optionsSummary };
    };
    function generateGroupContent(...groupsKeys) {
        var _a;
        const content = [];
        const optionsKeys = ['OwnerProgram', 'Accessories', 'Warranty'];
        const regularKeys = groupsKeys.filter(key => !optionsKeys.includes(key));
        const optionsKeysInRequest = groupsKeys.filter(key => optionsKeys.includes(key));
        const firstKey = regularKeys.slice(0, 1);
        firstKey.forEach(key => {
            const group = getSummaryGroupItemByKey(key);
            if (group)
                content.push(generateContentForGroup(group, key));
        });
        const { optionsTotal, optionsSummary } = generateSummaryForOptionsGroup(optionsKeysInRequest);
        if (optionsTotal > 0) {
            content.push({
                title: OptionsLabel.title,
                value: optionsTotal,
                unit: (_a = optionsSummary[0]) === null || _a === void 0 ? void 0 : _a.unit,
                dataTestId: `cy-breakdown-header-${OptionsLabel.key}`,
                tooltipText: '',
                content: optionsSummary.map(summaryItem => ({
                    title: summaryItem.title,
                    value: summaryItem.value,
                    unit: summaryItem.unit,
                    tooltipText: summaryItem.description,
                    dataTestId: `cy-breakdown-subItem-${OptionsLabel.key}-${summaryItem.key}`,
                    content: getSubGroupContent(summaryItem.key),
                })),
            });
        }
        const remainingKeys = regularKeys.slice(1);
        remainingKeys.forEach(key => {
            const group = getSummaryGroupItemByKey(key);
            if (group)
                content.push(generateContentForGroup(group, key));
        });
        return content;
    }
    const vehiclePriceContent = generateGroupContent('NetVehiclePrice')[0];
    const dueUponDeliveryContent = generateGroupContent('DueUponDeliveryGroup')[0];
    const additionalInformationContent = generateGroupContent('AdditionalInformationGroup')[0];
    const priceBeforeTaxes = !data ? 0 : Number(vehiclePriceContent === null || vehiclePriceContent === void 0 ? void 0 : vehiclePriceContent.value) - data.taxes;
    const showLuxuryTaxDisclaimer = !includeFees
        ? Number(vehiclePriceContent === null || vehiclePriceContent === void 0 ? void 0 : vehiclePriceContent.value) > LUXURY_TAX_MINIMUM
        : priceBeforeTaxes > LUXURY_TAX_MINIMUM;
    const groups = [
        {
            key: 'VehiclePricing',
            title: t('Pages.Bap.vehiclePricingLabel'),
            titleDataTestId: 'cy-vehicle-pricing-title',
            content: [
                ...generateGroupContent('MSRP', 'Accessories', 'Warranty', 'OwnerProgram', 'OptionsGroup', 'FeesGroup', 'SubTotal', 'TradeIn', 'TradeInOwing', 'TotalVehiclePrice', 'DownPayment', 'IncentivesGroup', 'GST', 'PST', 'LoyaltyDiscount'),
                {
                    ...vehiclePriceContent,
                    description: showLuxuryTaxDisclaimer ? t('Pages.Bap.luxuryTaxDisclaimerText') : '',
                    descriptionTestId: 'cy-tax-disclaimer-text',
                },
                ...generateGroupContent('RatePeriod'),
            ],
        },
        {
            key: 'LeaseInformation',
            title: t('Pages.Bap.leaseInformationLabel'),
            content: generateGroupContent('Term', 'TermMonths', 'AnnualLeaseRate', 'EffectiveApr', 'AnnualFinanceRate', 'PaymentAmountGroup', 'PaymentInformationGroup', 'LoyaltyDiscountRate', 'LoyaltyDiscountRateYouSave', 'AnnualLeaseExclusive', 'FirstMonthlyPayment', 'KilometerGroup', 'AdditionalKilometersCost'),
        },
        {
            key: 'DueUponDelivery',
            title: dueUponDeliveryContent === null || dueUponDeliveryContent === void 0 ? void 0 : dueUponDeliveryContent.title,
            content: dueUponDeliveryContent === null || dueUponDeliveryContent === void 0 ? void 0 : dueUponDeliveryContent.content,
            value: dueUponDeliveryContent === null || dueUponDeliveryContent === void 0 ? void 0 : dueUponDeliveryContent.value,
            unit: dueUponDeliveryContent === null || dueUponDeliveryContent === void 0 ? void 0 : dueUponDeliveryContent.unit,
            asAccordion: true,
        },
        {
            key: 'AdditionalInformation',
            title: additionalInformationContent === null || additionalInformationContent === void 0 ? void 0 : additionalInformationContent.title,
            value: additionalInformationContent === null || additionalInformationContent === void 0 ? void 0 : additionalInformationContent.value,
            unit: additionalInformationContent === null || additionalInformationContent === void 0 ? void 0 : additionalInformationContent.unit,
            content: additionalInformationContent === null || additionalInformationContent === void 0 ? void 0 : additionalInformationContent.content,
            asAccordion: true,
        },
    ];
    return {
        groups,
    };
};
export function getBreakdownGroupContentValue(value, unit, language) {
    if (!unit) {
        if (typeof value === 'number')
            return formatPrice(value, language, 0).replace('$', '');
        return value;
    }
    if (unit === 'text')
        return value;
    if (unit === 'percent')
        return `${value}%`;
    if (unit === 'number')
        return formatPrice(value, language, 0).replace('$', '');
    return formatPrice(value, language, 2);
}
export default usePriceBreakdownGroups;
